import { useMemo, useState, useRef, useEffect, useCallback, type ChangeEvent } from 'react'
import type { AnswerValue, WorkflowStage } from '../../types/workflow'
import { API_BASE_URL } from '@/lib/apiConfig';
interface BodyPhotoTemplateProps {
  stage: WorkflowStage
  patientId?: string
  existingKey?: string | null
  onSelect: (value: AnswerValue) => void
}

function createUuid() {
  return crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10MB

export default function BodyPhotoTemplate({ stage, patientId, existingKey, onSelect }: BodyPhotoTemplateProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedKey, setUploadedKey] = useState<string | null>(null)
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isCameraOpen, setIsCameraOpen] = useState(false)
  const [isCameraLoading, setIsCameraLoading] = useState(false)
  const [isFrontCamera, setIsFrontCamera] = useState(false)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null)
  const resolvedPatientId = useMemo(() => patientId || 'unknown-patient', [patientId])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const startCameraWithFacing = useCallback(async (front: boolean) => {
    try {
      setIsCameraLoading(true)
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: front ? 'user' : 'environment',
          width: { ideal: 1280 },
          height: { ideal: 1600 },
        },
      })
      setStream(mediaStream)
      setIsCameraOpen(true)
      setIsCameraLoading(false)
      setUploadError(null)
    } catch {
      setIsCameraLoading(false)
      setUploadError('Camera access is required. Please allow camera permissions or upload a file instead.')
    }
  }, [])

  const startCamera = useCallback(() => {
    void startCameraWithFacing(isFrontCamera)
  }, [isFrontCamera, startCameraWithFacing])

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream
    }
  }, [stream])

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      setStream(null)
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setIsCameraOpen(false)
    setIsCameraLoading(false)
  }, [stream])

  useEffect(() => {
    if (!existingKey) return

    const loadExistingPreview = async () => {
      try {
        const apiUrl = API_BASE_URL
        const response = await fetch(`${apiUrl}/uploads/view-url?key=${encodeURIComponent(existingKey)}`)
        const json = await response.json()
        if (json?.success && json?.data?.viewUrl) {
          setUploadedImage(json.data.viewUrl)
        } else {
          setUploadedImage(null)
        }
      } catch {
        setUploadedImage(null)
      } finally {
        setUploadedKey(existingKey)
        setUploadError(null)
      }
    }

    void loadExistingPreview()
  }, [existingKey])

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return
    const video = videoRef.current
    const canvas = canvasRef.current
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    if (isFrontCamera) {
      ctx.translate(canvas.width, 0)
      ctx.scale(-1, 1)
    }
    ctx.drawImage(video, 0, 0)

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((captured) => resolve(captured), 'image/jpeg', 0.85)
    })
    if (!blob) return

    if (uploadedImage?.startsWith('blob:')) URL.revokeObjectURL(uploadedImage)
    setUploadedImage(URL.createObjectURL(blob))
    setCapturedBlob(blob)
    setUploadedKey(null)
    setUploadError(null)
    stopCamera()
  }

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setUploadError('File too large. Please upload an image under 10MB.')
      return
    }
    if (uploadedImage?.startsWith('blob:')) URL.revokeObjectURL(uploadedImage)
    const blobUrl = URL.createObjectURL(file)
    setUploadedImage(blobUrl)
    setCapturedBlob(file)
    setUploadedKey(null)
    setUploadError(null)
  }

  const uploadBlob = async (blob: Blob) => {
    setIsUploading(true)
    setUploadError(null)
    try {
      const apiUrl = API_BASE_URL
      const filename = `${resolvedPatientId}-body-photo-${Date.now()}-${createUuid()}.jpg`
      const presignRes = await fetch(`${apiUrl}/uploads/public/document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename,
          contentType: blob.type || 'image/jpeg',
          documentType: 'glp1_body_photo',
        }),
      })
      const presignJson = await presignRes.json()
      if (!presignJson?.success) throw new Error('Failed to get upload URL')
      const uploadResponse = await fetch(presignJson.data.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': blob.type || 'image/jpeg',
          // Presigned URL from /uploads/public/document signs this header.
          'x-amz-server-side-encryption': 'AES256',
        },
        body: blob,
      })
      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed (${uploadResponse.status})`)
      }
      if (!presignJson?.data?.key) {
        throw new Error('Upload succeeded but no file key was returned')
      }
      return presignJson.data.key
    } finally {
      setIsUploading(false)
    }
  }

  const handleContinue = async () => {
    if (uploadedKey) {
      onSelect({ body_photo_key: uploadedKey })
      return
    }
    if (!capturedBlob) {
      setUploadError('Please upload or capture your body photo first.')
      return
    }
    try {
      const key = await uploadBlob(capturedBlob)
      setUploadedKey(key)
      onSelect({ body_photo_key: key })
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Upload failed. Please try again.')
    }
  }

  const handleRetake = () => {
    if (uploadedImage?.startsWith('blob:')) URL.revokeObjectURL(uploadedImage)
    setUploadedImage(null)
    setCapturedBlob(null)
    setUploadedKey(null)
    setUploadError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  useEffect(() => {
    return () => {
      if (stream) stream.getTracks().forEach((track) => track.stop())
      if (uploadedImage?.startsWith('blob:')) URL.revokeObjectURL(uploadedImage)
    }
  }, [stream, uploadedImage])

  return (
    <div className="flex-1 flex flex-col gap-4">
      {isCameraOpen ? (
        <div className="flex flex-col gap-3">
          <div className="relative aspect-[3/4] overflow-hidden rounded-xl bg-black">
            {isCameraLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/90">
                <div className="flex flex-col items-center gap-3">
                  <div className="h-10 w-10 animate-spin rounded-full border-2 border-[var(--ww-accent)] border-t-transparent" />
                  <p className="text-[13px] text-white/70">Loading camera...</p>
                </div>
              </div>
            )}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="h-full w-full object-cover"
              style={{ transform: isFrontCamera ? 'scaleX(-1)' : 'none' }}
            />
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute left-6 top-6 h-10 w-10 rounded-tl border-l-2 border-t-2 border-white/40" />
              <div className="absolute right-6 top-6 h-10 w-10 rounded-tr border-r-2 border-t-2 border-white/40" />
              <div className="absolute bottom-6 left-6 h-10 w-10 rounded-bl border-b-2 border-l-2 border-white/40" />
              <div className="absolute bottom-6 right-6 h-10 w-10 rounded-br border-b-2 border-r-2 border-white/40" />
              <div className="absolute bottom-12 left-0 right-0 text-center">
                <span className="rounded-full bg-black/40 px-3 py-1 text-[11px] text-white/60">Full body, head to toe</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              className="cursor-pointer rounded-xl border border-white/20 bg-white/[0.03] px-4 py-3 text-[14px] font-medium text-white/80 hover:bg-white/[0.06]"
              onClick={stopCamera}
              type="button"
            >
              Cancel
            </button>
            <button
              className="flex-1 cursor-pointer rounded-xl border border-white/20 bg-white/[0.03] px-4 py-3 text-[14px] font-medium text-white hover:bg-white/[0.06]"
              onClick={() => {
                const next = !isFrontCamera
                setIsFrontCamera(next)
                stopCamera()
                setTimeout(() => {
                  void startCameraWithFacing(next)
                }, 100)
              }}
              type="button"
            >
              Flip Camera
            </button>
            <button
              className="flex-1 cursor-pointer rounded-xl border-none bg-[var(--ww-btn-bg)] px-4 py-3 text-[14px] font-semibold text-[var(--ww-btn-text)]"
              onClick={() => {
                void capturePhoto()
              }}
              type="button"
            >
              Take Photo
            </button>
          </div>
          <canvas ref={canvasRef} className="hidden" />
        </div>
      ) : uploadedImage || uploadedKey ? (
        <div className="flex flex-col gap-3">
          {uploadedImage ? (
            <div className="overflow-hidden rounded-xl border border-white/20">
              <img src={uploadedImage} alt="Body photo preview" className="block h-auto w-full" />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-white/20 bg-white/[0.03] p-8">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-emerald-300"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <div className="text-center">
                <p className="mb-1 text-[15px] font-medium text-white">Photo uploaded</p>
                <p className="text-[13px] text-white/60">Your body photo was successfully uploaded</p>
              </div>
            </div>
          )}
          {uploadError && <div className="text-center text-[13px] text-red-400">{uploadError}</div>}
          <button
            className="self-center cursor-pointer border-none bg-transparent text-[13px] text-[var(--ww-accent)] hover:underline"
            onClick={handleRetake}
            disabled={isUploading}
            type="button"
          >
            Retake / Re-upload
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-[13px] leading-relaxed text-white/60">
            Please share a full body photo standing upright. This helps our medical team properly evaluate your treatment plan.
          </p>

          <div className="rounded-xl border border-white/20 bg-white/[0.03] p-3">
            <p className="mb-2 m-0 text-[11px] font-semibold uppercase tracking-[0.06em] text-white/40">Photo tips</p>
            <div className="flex flex-col gap-1.5">
              {['Stand in a well-lit area', 'Show full body from head to toe', 'Wear form-fitting clothing'].map((tip) => (
                <div key={tip} className="flex items-center gap-2 text-[12px] text-white/70">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0 text-[var(--ww-accent)]"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{tip}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-white/20 bg-white/[0.03] px-4 py-6 transition-all hover:bg-white/[0.06]"
              onClick={startCamera}
              type="button"
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[var(--ww-accent)]"
              >
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              <span className="text-[13px] font-medium text-white">Take Photo</span>
            </button>

            <button
              className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-white/20 bg-white/[0.03] px-4 py-6 transition-all hover:bg-white/[0.06]"
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[var(--ww-accent)]"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span className="text-[13px] font-medium text-white">Upload File</span>
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFileUpload}
          />
          {uploadError && <div className="text-center text-[13px] text-red-400">{uploadError}</div>}
        </div>
      )}

      <div className="mt-auto pb-2 pt-6 -ml-[42px] w-[calc(100%+42px)]">
        <button
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
          disabled={(!capturedBlob && !uploadedKey) || isUploading}
          onClick={() => {
            void handleContinue()
          }}
          type="button"
        >
          {isUploading ? 'Uploading...' : 'Continue'}
        </button>
      </div>
    </div>
  )
}
